import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToFirstCapital } from "../../Utils/helpers";
import {
  viewSiteVisitRental,
  statusSiteVisitRental,
} from "../../Redux/Actions/siteVisitActions";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import "./viewAgentSiteVisit.css";

const VALID_STATUSES = ["requested", "confirmed", "ongoing", "completed", "cancelled"];

const ViewSiteVisitRental = () => {
  const { id } = useParams();
  const { siteVisitRental } = useSelector((state) => state.siteVisit);
  const dispatch = useDispatch();

  const [tripStatus, setTripStatus] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [cancelledReason, setCancelledReason] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(viewSiteVisitRental(id)).finally(() => setLoading(false));
  }, [id, dispatch]);

  useEffect(() => {
    if (siteVisitRental?.data?.status) {
      setTripStatus(siteVisitRental.data.status);
    }
  }, [siteVisitRental]);

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setCancelledReason("");
    setShowConfirm(true);
  };

  const PropertyLocationMap = ({ coordinates, address }) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);

    useEffect(() => {
      if (!coordinates || coordinates.length !== 2) return;

      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
        document.head.appendChild(link);
      }

      const initMap = () => {
        if (mapInstanceRef.current || !mapRef.current) return;
        const L = window.L;

        // GeoJSON format: coordinates = [lng, lat]
        const latLng = [coordinates[1], coordinates[0]];

        const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false });
        mapInstanceRef.current = map;

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors",
          maxZoom: 19,
        }).addTo(map);

        const markerIcon = L.divIcon({
          html: `<div class="vsv-map-marker vsv-map-marker--drop">
                 <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                   <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                   <circle cx="12" cy="10" r="3" fill="white"/>
                 </svg>
               </div>`,
          className: "",
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });

        L.marker(latLng, { icon: markerIcon })
          .addTo(map)
          .bindPopup(`<b>Property Location</b>${address ? `<br/>${address}` : ""}`);

        map.setView(latLng, 16);
      };

      if (window.L) {
        initMap();
      } else {
        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
        script.onload = initMap;
        document.head.appendChild(script);
      }

      return () => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        }
      };
    }, [coordinates, address]);

    if (!coordinates || coordinates.length !== 2) return null;

    return <div ref={mapRef} className="vsv-map-container" style={{ height: 220, marginTop: 12, borderRadius: 8, overflow: "hidden" }} />;
  };

  const confirmStatusChange = async () => {
    if (selectedStatus === "cancelled" && !cancelledReason.trim()) {
      toast.error("Cancelled reason is required");
      return;
    }
    setIsUpdating(true);
    try {
      await dispatch(
        statusSiteVisitRental({
          id: data?._id,
          status: selectedStatus,
          ...(selectedStatus === "cancelled" && { cancelled_reason: cancelledReason.trim() }),
        })
      );
      toast.success("Status updated successfully!");
      await dispatch(viewSiteVisitRental(id));
      setTripStatus(selectedStatus);
      setShowConfirm(false);
    } catch {
      toast.error("Error updating status");
    } finally {
      setIsUpdating(false);
    }
  };

  const data = siteVisitRental?.data;

  if (loading) {
    return (
      <MainLayout>
        <div className="vsv-loader-wrap">
          <div className="vsv-loader" />
          <p className="vsv-loader-text">Loading site visit data…</p>
        </div>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <div className="vsv-empty-wrap">
          <div className="vsv-empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <p className="vsv-empty-text">No Site Visit Data Found.</p>
          <Link to="/site-visit-rental" className="vsv-back-link">← Back to Site Visits</Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="vsv-wrapper">

        {/* ── Header ── */}
        <div className="vsv-header">
          <div className="vsv-header-left">
            <Link to="/site-visit-rental" className="vsv-back-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </Link>
            <div className="vsv-header-divider" />
            <div>
              <div className="vsv-breadcrumb">Site Visit Rental / List / View</div>
            </div>
          </div>
          <div className={`vsv-status-badge vsv-status-badge--${data?.status?.toLowerCase()}`}>
            {ToFirstCapital(data?.status || "")}
          </div>
        </div>

        {/* ── Hero Card ── */}
        <div className="vsv-hero-card">
          <div className="vsv-hero-left">
            <div>
              <div className="vsv-hero-label">Property</div>
              <div className="vsv-hero-value">{data?.property?.property_name || "—"}</div>
            </div>
            {data?.property?.cover_photos?.[0]?.url && (
              <img
                src={data.property.cover_photos[0].url}
                alt={data.property.property_name}
                style={{ width: 64, height: 64, borderRadius: 8, objectFit: "cover", border: "2px solid #e5e7eb" }}
              />
            )}
          </div>
          <div className="vsv-hero-meta">
            <div className="vsv-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {data?.site_visit_date_time
                ? new Date(data.site_visit_date_time).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
                : "—"}
            </div>
            <div className="vsv-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
              </svg>
              {data?.user?.name || "—"}
            </div>
            <div className="vsv-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              {data?.timeSlots || "—"}
            </div>
          </div>
        </div>

        {/* ── Main Grid ── */}
        <div className="vsv-grid-layout">

          {/* Left Column */}
          <div className="vsv-col">

            {/* User Info */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">01</span>
                <h2 className="vsv-card-title">User Information</h2>
              </div>
              <div className="vsv-info-grid">
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Full Name</span>
                  <span className="vsv-info-value">{data?.user?.name || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Email</span>
                  <span className="vsv-info-value">{data?.user?.email || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Contact Number</span>
                  <span className="vsv-info-value">
                    {data?.user?.country_code} {data?.user?.contact_number || "No Data"}
                  </span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Status</span>
                  <span className={`vsv-inline-status vsv-inline-status--${data?.status?.toLowerCase()}`}>
                    {ToFirstCapital(data?.status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Visit Details */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">02</span>
                <h2 className="vsv-card-title">Visit Details</h2>
              </div>
              <div className="vsv-info-grid">
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Property</span>
                  <span className="vsv-info-value">{data?.property?.property_name || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Property ID</span>
                  <span className="vsv-info-value">{data?.property?.property_id || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Time Slot</span>
                  <span className="vsv-info-value">{data?.timeSlots || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Visit Date</span>
                  <span className="vsv-info-value">{data?.site_visit_date_time
                    ? new Date(data.site_visit_date_time).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                    : "—"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Uploaded On</span>
                  <span className="vsv-info-value">
                    {data?.created_at ? new Date(data.created_at).toLocaleString() : "No Data"}
                  </span>
                </div>
              </div>
            </div>

            {/* Cancelled Reason */}
            {data?.status === "cancelled" && data?.cancelled_reason && (
              <div className="vsv-card">
                <div className="vsv-card-header">
                  <span className="vsv-card-badge">03</span>
                  <h2 className="vsv-card-title">Cancellation Reason</h2>
                </div>
                <p className="vsv-purpose-text">{data.cancelled_reason}</p>
              </div>
            )}

          </div>

          {/* Right Column */}
          <div className="vsv-col">

            {/* Property Address */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">04</span>
                <h2 className="vsv-card-title">Property Location</h2>
              </div>
              <div className="vsv-address-row">
                <div className="vsv-address-item">
                  <div className="vsv-address-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div>
                    <div className="vsv-address-label">Property Address</div>
                    <div className="vsv-address-val">{data?.property?.street_address || "No Data"}</div>
                  </div>
                </div>
              </div>

              <PropertyLocationMap
                coordinates={data?.property?.location?.coordinates}
                address={data?.property?.street_address}
              />
            </div>

            {/* Trip Status */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">05</span>
                <h2 className="vsv-card-title">Visit Status</h2>
              </div>
              <div className="vsv-info-item" style={{ marginBottom: "12px" }}>
                <span className="vsv-info-label">Current Status</span>
                <span className={`vsv-inline-status vsv-inline-status--${data?.status?.toLowerCase()}`} style={{ marginTop: "4px" }}>
                  {ToFirstCapital(data?.status)}
                </span>
              </div>
              <div className="vsv-info-item">
                <span className="vsv-info-label">Update Status</span>
                <div className="vsv-select-wrap" style={{ marginTop: "6px" }}>
                  <select
                    value={tripStatus}
                    onChange={handleStatusChange}
                    className="vsv-select"
                    disabled={isUpdating}
                  >
                    <option value="" disabled>Select Status</option>
                    {VALID_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </select>
                  <svg className="vsv-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {isUpdating && (
                  <div className="vsv-updating-text">
                    <div className="vsv-updating-dot" />
                    Updating status…
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Confirm Modal ── */}
      {showConfirm && (
        <div className="vsv-modal-backdrop">
          <div className="vsv-modal">
            <div className="vsv-modal-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 className="vsv-modal-title">Confirm Status Change</h3>
            <p className="vsv-modal-text">
              Are you sure you want to change the status to{" "}
              <strong>"{ToFirstCapital(selectedStatus)}"</strong>?
            </p>

            {selectedStatus === "cancelled" && (
              <textarea
                className="vsv-select"
                style={{ width: "100%", marginTop: 10, minHeight: 70, resize: "vertical" }}
                placeholder="Enter cancellation reason (required)"
                value={cancelledReason}
                onChange={(e) => setCancelledReason(e.target.value)}
              />
            )}

            <div className="vsv-modal-actions">
              <button
                className="vsv-btn vsv-btn--confirm"
                onClick={confirmStatusChange}
                disabled={isUpdating}
              >
                Confirm
              </button>
              <button
                className="vsv-btn vsv-btn--cancel"
                onClick={() => setShowConfirm(false)}
                disabled={isUpdating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ViewSiteVisitRental;